import { getCountryForTimezone } from 'countries-and-timezones';
import { useFormatUtils } from '../../../hooks/useFormatUtils';
import type { DeviceInfo } from '../../../types/commonTypes';

const PLATFORM_LABELS: Record<string, string> = { ios: 'iOS', android: 'Android' };

const PERMISSION_COLORS: Record<string, string> = {
    granted: 'text-green-600',
    denied: 'text-red-600',
};

/** "Romania (RO)", or the bare code if the browser has no name for it. */
const countryLabel = (code: string): string => {
    try {
        const name = new Intl.DisplayNames(['en'], { type: 'region' }).of(code);
        return name && name !== code ? `${name} (${code})` : code;
    } catch {
        return code;
    }
};

/**
 * The region the user picked with their language, e.g. GB in `en-GB`. Not maximized: that
 * would invent US for a bare `en`.
 */
const localeRegion = (locale: string): string | undefined => {
    try {
        return new Intl.Locale(locale).region;
    } catch {
        return undefined;
    }
};

const Row = ({ label, value, className = '' }: { label: string; value: string; className?: string }) => (
    <p className="mb-2">
        {label}: <span className={`font-bold ${className}`}>{value}</span>
    </p>
);

interface DeviceInfoCardProps {
    deviceInfo?: DeviceInfo;
    timezone?: string;
}

/**
 * Last seen and device facts, to put the rest of the profile in context. There is no IP
 * country from the app, so the timezone country is the main location signal. Phones set
 * the timezone from the network, so it follows the user; for a timezone that several
 * countries share, it shows the main one.
 */
export const DeviceInfoCard = ({ deviceInfo, timezone }: DeviceInfoCardProps) => {
    const { formatTimeSince } = useFormatUtils();

    const timezoneCountry = timezone ? getCountryForTimezone(timezone) : null;
    const region = deviceInfo?.locale ? localeRegion(deviceInfo.locale) : undefined;
    const platform = deviceInfo?.platform
        ? `${PLATFORM_LABELS[deviceInfo.platform] ?? deviceInfo.platform} ${deviceInfo.osVersion ?? ''}`.trim()
        : undefined;
    const device = [deviceInfo?.manufacturer, deviceInfo?.model].filter(Boolean).join(' ');

    return (
        <div className="bg-white rounded-lg shadow-md p-4 sm:p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Device & Activity</h2>
            <div className="text-sm text-gray-800">
                {!deviceInfo && (
                    <p className="mb-2 text-gray-600">
                        No device info yet. The app sends it on start, from the version with device info.
                    </p>
                )}

                {deviceInfo?.source === 'revenuecat' && (
                    <p className="mb-4 p-3 bg-amber-50 border border-amber-300 text-amber-800 rounded">
                        From the one-time RevenueCat backfill. The app has not sent device info since.
                    </p>
                )}

                {deviceInfo?.lastSeenAt && (
                    <Row
                        label="Last Seen"
                        value={`${deviceInfo.lastSeenAt.toLocaleString()} (${formatTimeSince(deviceInfo.lastSeenAt.getTime())})`}
                    />
                )}
                {deviceInfo?.appVersion && (
                    <Row
                        label="App Version"
                        value={deviceInfo.appBuild ? `${deviceInfo.appVersion} (${deviceInfo.appBuild})` : deviceInfo.appVersion}
                    />
                )}
                {platform && <Row label="Platform" value={platform} />}
                {device && <Row label="Device" value={device} />}
                {deviceInfo?.isVirtual && (
                    <Row label="Simulator / Emulator" value="Yes" className="text-amber-600" />
                )}
                {deviceInfo?.webViewVersion && <Row label="WebView Version" value={deviceInfo.webViewVersion} />}
                {deviceInfo?.notificationPermission && (
                    <Row
                        label="Notification Permission"
                        value={deviceInfo.notificationPermission}
                        className={PERMISSION_COLORS[deviceInfo.notificationPermission] ?? ''}
                    />
                )}

                {deviceInfo?.locale && (
                    <Row
                        label="Locale"
                        value={region ? `${deviceInfo.locale} (region: ${countryLabel(region)})` : deviceInfo.locale}
                    />
                )}
                {timezone && <Row label="Timezone" value={timezone} />}
                {timezoneCountry && <Row label="Country (from timezone)" value={countryLabel(timezoneCountry.id)} />}
                {deviceInfo?.country && (
                    <Row label="Country (RevenueCat, from IP)" value={countryLabel(deviceInfo.country)} />
                )}
            </div>
        </div>
    );
};
